import React, { CSSProperties, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import Autocomplete from '@embracesbs/component-autocomplete'
import Button2 from '@embracesbs/component-button2/Button2'
import Icon from '@embracesbs/component-icon'
import Text from '@embracesbs/component-text'
import TextField from '@embracesbs/component-textfield'
import { useApi } from '../Hooks'
import {
  ConfigFilter,
  ModuleFilter,
  SearchInterpretation,
  Subscription,
  Tenant,
  TenantConfigDisplayValue,
  TenantConfigFilterKey,
  TenantConfigValues,
  TenantModuleVersions
} from '../Interfaces/Types'
import SectionHeader from '../Components/Common/SectionHeader'
import TenantListItem from '../Components/Tenants/TenantListItem'
import EmptyState from '@embracesbs/component-emptystate'
import LoadingState from '../Components/Common/LoadingState'
import {
  CLIENT_SECRET_WARNING_DAYS,
  apiErrorMessage,
  clientSecretExpiringBoundUtcIso,
  msUntilNextLocalMidnight
} from '../Utilities/helpers/helpers'

const filterRowStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: '8px',
  marginBottom: '8px'
}

const chipsWrapStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '6px'
}

const removeKeyButtonStyle: CSSProperties = {
  border: 'none',
  background: 'none',
  color: 'inherit',
  cursor: 'pointer',
  fontSize: '15px',
  lineHeight: 1,
  padding: '0 3px'
}

// A query string cannot carry an absent value, so "the tenant has no value for this
// key" travels as a literal. Byte-identical to the API's ConfigValuesResponse.UnsetValue
// (WI 159594).
const UNSET_CONFIG_VALUE = '__unset'

// A boolean key needs no value list fetched — these three are everything it can be.
const BOOLEAN_CONFIG_VALUES = ['true', 'false', UNSET_CONFIG_VALUE]

// Marks a selected module version as excluded rather than included. Byte-identical to
// the API's ModuleVersionsResponse.NegationPrefix; a package version can never start
// with it, so it cannot collide with a real value.
const NEGATED_MODULE_PREFIX = '!'

const chipLabel = (value: string) => (value === UNSET_CONFIG_VALUE ? 'Not set' : value)

// The search matches stages, statuses and configuration values case-insensitively,
// so a value restored from a shared URL or set by the AI assistant is the same filter
// as the chip it corresponds to, whatever its casing.
const sameValue = (a: string, b: string) => a.toLowerCase() === b.toLowerCase()

// "<key>:<value>", one entry per selected value — the shape both the URL and /api/search
// take. Values are never joined into one entry: a stored value legitimately contains a
// comma (fe-embrace-available-languages is "en-GB,nl-NL"), and any separator would split
// such a value into two that match nothing (WI 159594).
const parseConfigFilters = (entries: string[]): ConfigFilter[] => {
  const filters: ConfigFilter[] = []

  entries.forEach((entry) => {
    // A value may contain a colon, a key may not, so only the first one separates.
    const separator = entry.indexOf(':')
    if (separator < 1) return

    const key = entry.slice(0, separator)
    const value = entry.slice(separator + 1)
    if (!value) return

    // Repeated entries for one key merge into that key's single selected-value set.
    const existing = filters.find((filter) => filter.key === key)
    if (!existing) {
      filters.push({ key, values: [value] })
      return
    }
    if (!existing.values.some((selected) => sameValue(selected, value))) existing.values.push(value)
  })

  return filters
}

// "<package>:<version>", one entry per selected version — the shape both the URL and
// /api/search take, with a leading "!" on the version when the selection is negated.
// The page writes one mode per package, so the "!" repeats on every one of its entries.
// A hand-written URL may mix both for one package, which the API reads as "is one of the
// included AND none of the excluded"; that is the included versions minus the excluded
// ones, so it is restored as exactly that rather than flipped to "none of them".
const parseModuleFilters = (entries: string[]): ModuleFilter[] => {
  const byPackage: { package: string; included: string[]; excluded: string[] }[] = []

  entries.forEach((entry) => {
    // A version may not contain a colon and a package name may not either, but only
    // the first one separates — the same rule the configuration filter follows.
    const separator = entry.indexOf(':')
    if (separator < 1) return

    const pkg = entry.slice(0, separator)
    let value = entry.slice(separator + 1)
    const negate = value.startsWith(NEGATED_MODULE_PREFIX)
    if (negate) value = value.slice(NEGATED_MODULE_PREFIX.length)
    if (!value) return

    // Package names match case-insensitively, as they do in the API.
    let existing = byPackage.find((filter) => sameValue(filter.package, pkg))
    if (!existing) {
      existing = { package: pkg, included: [], excluded: [] }
      byPackage.push(existing)
    }
    const list = negate ? existing.excluded : existing.included
    if (!list.some((selected) => sameValue(selected, value))) list.push(value)
  })

  return byPackage.map(({ package: pkg, included, excluded }) => {
    if (included.length === 0) return { package: pkg, versions: excluded, negate: true }
    // Included versions that are also excluded can't match; if that leaves none, nothing
    // can match at all, and keeping the included ones is the closest the page can show.
    const remaining = included.filter((version) => !excluded.some((other) => sameValue(other, version)))
    return { package: pkg, versions: remaining.length > 0 ? remaining : included, negate: false }
  })
}

const AdvancedSearch: React.FC = () => {
  const navigate = useNavigate()
  const { fetchApi } = useApi()
  const [searchParams, setSearchParams] = useSearchParams()

  // The full search state lives in the URL (?q=&stage=&status=&config=) so searches
  // are shareable and survive browser back-navigation.
  const [query, setQuery] = useState(searchParams.get('q') || '')
  const [environmentOptions, setEnvironmentOptions] = useState<string[]>([])
  const [deployStatusOptions, setDeployStatusOptions] = useState<string[]>([])
  const [configKeys, setConfigKeys] = useState<TenantConfigFilterKey[]>([])
  const [selectedEnvironments, setSelectedEnvironments] = useState<string[]>(
    () => searchParams.get('stage')?.split(',').filter(Boolean) ?? []
  )
  const [selectedDeployStatuses, setSelectedDeployStatuses] = useState<string[]>(
    () => searchParams.get('status')?.split(',').filter(Boolean) ?? []
  )
  const [configFilters, setConfigFilters] = useState<ConfigFilter[]>(() => parseConfigFilters(searchParams.getAll('config')))
  const [configValueOptions, setConfigValueOptions] = useState<Record<string, string[]>>({})
  const [modulePackages, setModulePackages] = useState<string[]>([])
  const [moduleFilters, setModuleFilters] = useState<ModuleFilter[]>(() => parseModuleFilters(searchParams.getAll('module')))
  const [moduleVersionOptions, setModuleVersionOptions] = useState<Record<string, string[]>>({})
  const [includeDeleted, setIncludeDeleted] = useState<boolean>(() => searchParams.get('deleted') === '1')
  // The URL keeps only the toggle, not a date: the bound is recomputed from the viewer's
  // own today on every search, so a shared link means "expiring as of when it is opened".
  const [secretExpiring, setSecretExpiring] = useState<boolean>(() => searchParams.get('secretExpiring') === '1')
  // Bumped at local midnight while the expiring filter is on, so a page left open re-runs the
  // search with the new day's bound instead of keeping yesterday's result set.
  const [localDayTick, setLocalDayTick] = useState(0)
  const [results, setResults] = useState<{
    subscriptions?: Subscription[]
    tenants?: Tenant[]
    tenantConfigValues?: TenantConfigValues
    tenantModuleVersions?: TenantModuleVersions
  }>({})
  const [isLoading, setIsLoading] = useState(false)
  const [searchError, setSearchError] = useState<string | null>(null)
  const requestedConfigKeys = useRef<Set<string>>(new Set())
  const configValuesAbort = useRef<AbortController | null>(null)
  const requestedModulePackages = useRef<Set<string>>(new Set())
  const moduleVersionsAbort = useRef<AbortController | null>(null)

  // AI search assistant (NexusApi): the operator describes the tenants they want in
  // a sentence and the API answers with filters, which replace the current
  // selection below. Offered only when this segment has a Nexus connection configured.
  const [assistantEnabled, setAssistantEnabled] = useState(false)
  const [assistantPrompt, setAssistantPrompt] = useState('')
  const [assistantBusy, setAssistantBusy] = useState(false)
  const [assistantNote, setAssistantNote] = useState<{ text: string; isError: boolean } | null>(null)

  const configKeyMap = useMemo(() => new Map(configKeys.map((entry) => [entry.key, entry])), [configKeys])

  // One entry per selected value; a picked key with nothing selected contributes none, so
  // it is left out of both the URL and the request.
  const configQueryEntries = useMemo(
    () => configFilters.flatMap((filter) => filter.values.map((value) => `${filter.key}:${value}`)),
    [configFilters]
  )

  // Same shape for modules, with the package's mode stamped on every one of its
  // entries so the URL carries it without a parameter of its own.
  const moduleQueryEntries = useMemo(
    () =>
      moduleFilters.flatMap((filter) =>
        filter.versions.map((version) => `${filter.package}:${filter.negate ? NEGATED_MODULE_PREFIX : ''}${version}`)
      ),
    [moduleFilters]
  )

  // Keep the URL in sync with the search state (replace: typing shouldn't
  // spam the browser history — the single /search entry always holds the
  // latest criteria).
  useEffect(() => {
    const params = new URLSearchParams()
    if (query) params.set('q', query)
    if (selectedEnvironments.length > 0) params.set('stage', selectedEnvironments.join(','))
    if (selectedDeployStatuses.length > 0) params.set('status', selectedDeployStatuses.join(','))
    if (includeDeleted) params.set('deleted', '1')
    if (secretExpiring) params.set('secretExpiring', '1')
    configQueryEntries.forEach((entry) => params.append('config', entry))
    moduleQueryEntries.forEach((entry) => params.append('module', entry))
    setSearchParams(params, { replace: true })
  }, [
    query,
    selectedEnvironments,
    selectedDeployStatuses,
    includeDeleted,
    secretExpiring,
    configQueryEntries,
    moduleQueryEntries,
    setSearchParams
  ])

  useEffect(() => {
    // Kept in step with the search set, so the status chips do not offer a value that
    // only exists on rows the results exclude.
    const filterParams = includeDeleted ? '?includeDeleted=true' : ''
    fetchApi({ endpoint: `/api/search/availableFilters${filterParams}`, method: 'GET' })
      .then((response) => {
        if (response) {
          setEnvironmentOptions(response.environments || [])
          setDeployStatusOptions(response.deployStatuses || [])
          setConfigKeys(response.configKeys || [])
          setModulePackages(response.modulePackages || [])
          setAssistantEnabled(!!response.searchAssistantEnabled)
        }
      })
      .catch((error) => console.error(error))
  }, [fetchApi, includeDeleted])

  // A value list is computed over the same tenant set the search runs against, so
  // toggling deleted tenants invalidates every list already fetched. Declared ahead of
  // the fetch below so the two run in that order on a toggle, and it owns their abort
  // controller: a request still in flight across the toggle would otherwise write the
  // previous tenant set's values back over the cleared list.
  useEffect(() => {
    const controller = new AbortController()
    configValuesAbort.current = controller
    requestedConfigKeys.current = new Set()
    setConfigValueOptions({})
    return () => controller.abort()
  }, [includeDeleted])

  // The module version lists are computed over the same tenant set, so they are
  // invalidated on a deleted-toggle for exactly the reason the configuration ones are.
  useEffect(() => {
    const controller = new AbortController()
    moduleVersionsAbort.current = controller
    requestedModulePackages.current = new Set()
    setModuleVersionOptions({})
    return () => controller.abort()
  }, [includeDeleted])

  useEffect(() => {
    const deletedParam = includeDeleted ? '&includeDeleted=true' : ''
    configFilters.forEach(({ key }) => {
      const meta = configKeyMap.get(key)
      // No catalog entry yet means the value type is unknown — the reload once it
      // arrives decides whether this key needs a list at all.
      if (!meta || meta.valueType === 'boolean' || requestedConfigKeys.current.has(key)) return

      requestedConfigKeys.current.add(key)
      fetchApi({
        endpoint: `/api/search/configValues?key=${encodeURIComponent(key)}${deletedParam}`,
        method: 'GET',
        signal: configValuesAbort.current?.signal
      })
        .then((response) => setConfigValueOptions((current) => ({ ...current, [key]: response?.values ?? [] })))
        .catch((error) => {
          if (error?.name !== 'AbortError') console.error(error)
        })
    })
  }, [configFilters, configKeyMap, includeDeleted, fetchApi])

  useEffect(() => {
    const deletedParam = includeDeleted ? '&includeDeleted=true' : ''
    moduleFilters.forEach(({ package: pkg }) => {
      if (requestedModulePackages.current.has(pkg)) return

      requestedModulePackages.current.add(pkg)
      fetchApi({
        endpoint: `/api/search/moduleVersions?package=${encodeURIComponent(pkg)}${deletedParam}`,
        method: 'GET',
        signal: moduleVersionsAbort.current?.signal
      })
        .then((response) => setModuleVersionOptions((current) => ({ ...current, [pkg]: response?.versions ?? [] })))
        .catch((error) => {
          if (error?.name !== 'AbortError') console.error(error)
        })
    })
  }, [moduleFilters, includeDeleted, fetchApi])

  useEffect(() => {
    const hasCriteria =
      query ||
      selectedEnvironments.length > 0 ||
      selectedDeployStatuses.length > 0 ||
      secretExpiring ||
      configQueryEntries.length > 0 ||
      moduleQueryEntries.length > 0
    if (!hasCriteria) {
      setResults({})
      setSearchError(null)
      return
    }

    setIsLoading(true)
    setSearchError(null)
    const queryParams = new URLSearchParams({
      query,
      ...(selectedEnvironments.length > 0 && { environment: selectedEnvironments.join(',') }),
      ...(selectedDeployStatuses.length > 0 && { deployStatus: selectedDeployStatuses.join(',') }),
      ...(includeDeleted && { includeDeleted: 'true' }),
      ...(secretExpiring && { clientSecretExpiresBeforeUtc: clientSecretExpiringBoundUtcIso() })
    })
    // Repeated once per selected value: the API ORs the values inside a key and ANDs
    // across keys. Module entries follow the same rule, with a negated entry excluding
    // its version rather than including it.
    configQueryEntries.forEach((entry) => queryParams.append('config', entry))
    moduleQueryEntries.forEach((entry) => queryParams.append('module', entry))

    const handler = setTimeout(() => {
      fetchApi({ endpoint: `/api/search/search?${queryParams.toString()}`, method: 'GET' })
        .then((data) => setResults(data || {}))
        .catch((error) => {
          console.error(error)
          // Keeping the previous rows up would answer a different query than the one on
          // screen, with nothing saying the search failed.
          setResults({})
          // Surface the server's message (e.g. an unknown configuration key) when it sent one.
          setSearchError(apiErrorMessage(error, 'Could not run the search.'))
        })
        .finally(() => setIsLoading(false))
    }, 300)

    return () => clearTimeout(handler)
  }, [
    query,
    selectedEnvironments,
    selectedDeployStatuses,
    includeDeleted,
    secretExpiring,
    localDayTick,
    configQueryEntries,
    moduleQueryEntries,
    fetchApi
  ])

  useEffect(() => {
    if (!secretExpiring) return
    // A second past midnight, so the new day is certain when the bound is recomputed.
    const timer = setTimeout(() => setLocalDayTick((tick) => tick + 1), msUntilNextLocalMidnight() + 1000)
    return () => clearTimeout(timer)
  }, [secretExpiring, localDayTick])

  const toggle = (value: string, list: string[], setList: (v: string[]) => void) => {
    setList(list.some((v) => sameValue(v, value)) ? list.filter((v) => !sameValue(v, value)) : [...list, value])
  }

  const renderChips = (options: string[], selected: string[], setSelected: (v: string[]) => void, labelFor?: (value: string) => string) => (
    <div style={chipsWrapStyle}>
      {options.map((opt) => (
        <Button2
          key={opt}
          text={labelFor ? labelFor(opt) : opt}
          variant='filter'
          isActive={selected.some((value) => sameValue(value, opt))}
          onClick={() => toggle(opt, selected, setSelected)}
          type='button'
        />
      ))}
    </div>
  )

  const addConfigKey = (key: string) => {
    if (!key) return
    setConfigFilters((current) => (current.some((filter) => filter.key === key) ? current : [...current, { key, values: [] }]))
  }

  const removeConfigKey = (key: string) => setConfigFilters((current) => current.filter((filter) => filter.key !== key))

  const setConfigValues = (key: string, values: string[]) =>
    setConfigFilters((current) => current.map((filter) => (filter.key === key ? { ...filter, values } : filter)))

  const configKeyLabel = (key: string) => configKeyMap.get(key)?.label ?? key

  const configChipsFor = (filter: ConfigFilter): string[] => {
    const options = configKeyMap.get(filter.key)?.valueType === 'boolean' ? BOOLEAN_CONFIG_VALUES : (configValueOptions[filter.key] ?? [])
    // A value restored from the URL stays visible while its list is still in flight, and
    // folds into the chip it matches once the list arrives rather than doubling it.
    return [...options, ...filter.values.filter((value) => !options.some((option) => sameValue(option, value)))]
  }

  const addModulePackage = (pkg: string) => {
    if (!pkg) return
    setModuleFilters((current) =>
      current.some((filter) => sameValue(filter.package, pkg)) ? current : [...current, { package: pkg, versions: [], negate: false }]
    )
  }

  const removeModulePackage = (pkg: string) => setModuleFilters((current) => current.filter((filter) => filter.package !== pkg))

  const setModuleVersions = (pkg: string, versions: string[]) =>
    setModuleFilters((current) => current.map((filter) => (filter.package === pkg ? { ...filter, versions } : filter)))

  const toggleModuleNegate = (pkg: string) =>
    setModuleFilters((current) => current.map((filter) => (filter.package === pkg ? { ...filter, negate: !filter.negate } : filter)))

  const moduleChipsFor = (filter: ModuleFilter): string[] => {
    const options = moduleVersionOptions[filter.package] ?? []
    // A version restored from the URL stays visible while its list is still in flight,
    // and folds into the chip it matches once the list arrives rather than doubling it.
    return [...options, ...filter.versions.filter((version) => !options.some((option) => sameValue(option, version)))]
  }

  const modulePackageOptions = modulePackages
    .filter((pkg) => !moduleFilters.some((filter) => sameValue(filter.package, pkg)))
    .map((pkg) => ({ label: pkg, value: pkg }))

  const configKeyOptions = configKeys
    .filter((entry) => !configFilters.some((filter) => filter.key === entry.key))
    .map((entry) => ({ label: entry.label, value: entry.key }))

  // Only the keys the search actually filtered on come back, in the order the operator
  // picked them.
  const configValuesFor = (tenantId: string): TenantConfigDisplayValue[] => {
    const values = results.tenantConfigValues?.[tenantId]
    if (!values) return []

    return configFilters
      .filter((filter) => filter.key in values)
      .map((filter) => ({ label: configKeyLabel(filter.key), value: values[filter.key] }))
  }

  // Rendered on the same result-card line as the configuration values, in the order the
  // operator picked the packages.
  const moduleValuesFor = (tenantId: string): TenantConfigDisplayValue[] => {
    const versions = results.tenantModuleVersions?.[tenantId]
    if (!versions) return []

    return moduleFilters
      .filter((filter) => filter.package in versions)
      .map((filter) => ({ label: filter.package, value: versions[filter.package] }))
  }

  const askAssistant = async () => {
    const prompt = assistantPrompt.trim()
    if (!prompt || assistantBusy) return

    setAssistantBusy(true)
    setAssistantNote(null)
    try {
      const interpretation: SearchInterpretation | null | undefined = await fetchApi({
        endpoint: '/api/search/assistant/interpret',
        method: 'POST',
        body: { prompt }
      })
      // The hook answers nothing (rather than throwing) without a token or on a body it
      // cannot parse; the operator still needs to see that nothing happened.
      if (!interpretation) {
        setAssistantNote({ text: 'The AI assistant did not answer. Try again.', isError: true })
        return
      }

      // The answer replaces the whole selection: the sentence described everything the
      // operator wants, so a chip left over from the previous search would silently
      // narrow it. Every value here passed the API's catalog check, so the chips light
      // up exactly as if they had been clicked.
      setQuery(interpretation.query ?? '')
      setSelectedEnvironments(interpretation.environments)
      setSelectedDeployStatuses(interpretation.deployStatuses)
      setIncludeDeleted(interpretation.includeDeleted)
      setSecretExpiring(false)
      setConfigFilters(interpretation.configFilters)
      setModuleFilters(interpretation.moduleFilters ?? [])

      // The notes already say what was not applied and why.
      setAssistantNote({ text: [interpretation.explanation, ...interpretation.unresolved].join(' '), isError: false })
    } catch (error: any) {
      console.error(error)
      // The API's message says whether to retry (busy, not configured) or to rephrase;
      // anything else gets a generic line.
      setAssistantNote({ text: apiErrorMessage(error, 'The AI assistant could not interpret the request.'), isError: true })
    } finally {
      setAssistantBusy(false)
    }
  }

  const subscriptions = [...(results?.subscriptions ?? [])].sort((a, b) =>
    a.customerName.localeCompare(b.customerName, undefined, { sensitivity: 'base' })
  )
  const tenants = [...(results?.tenants ?? [])].sort((a, b) => a.tenantName.localeCompare(b.tenantName, undefined, { sensitivity: 'base' }))
  const hasCriteria =
    !!query ||
    selectedEnvironments.length > 0 ||
    selectedDeployStatuses.length > 0 ||
    secretExpiring ||
    configQueryEntries.length > 0 ||
    moduleQueryEntries.length > 0
  const hasResults = subscriptions.length > 0 || tenants.length > 0

  return (
    <div style={{ display: 'flex', flexDirection: 'row', height: 'var(--app-height, 100vh)', overflow: 'hidden' }}>
      {/* Left column: title band + search and filters. */}
      <div style={{ width: '30%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Dark band matching the other pages, with the back action and page title. */}
        <div
          style={{
            flexShrink: 0,
            height: '52px',
            boxSizing: 'border-box',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            padding: '0 10px',
            backgroundColor: 'var(--tma-band-bg)'
          }}
        >
          <Button2
            leftElement={<Icon iconName='ArrowLeft1' size={16} color='#ffffff' />}
            onClick={() => navigate('/')}
            text=''
            aria-label='Back to dashboard'
            type='button'
            variant='subtle'
          />
          <span
            style={{
              color: '#ffffff',
              fontSize: '14px',
              fontWeight: 600,
              fontFamily: "'F-Default', Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
            }}
          >
            Advanced search
          </span>
        </div>
        {/* Flush white bar with the search field and filters. */}
        <div
          style={{
            flexShrink: 0,
            background: 'var(--tma-panel-bg)',
            borderBottom: '1px solid var(--tma-list-card-border)',
            padding: '10px'
          }}
        >
          {assistantEnabled && (
            <div style={{ marginBottom: '10px', paddingBottom: '10px', borderBottom: '1px solid var(--tma-list-card-border)' }}>
              {/* Enter in the text input submits; the keydown bubbles up here from inside the
                  design-system field. Only the input counts: Enter on the field's clear button
                  must clear, not submit what was just discarded. */}
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') {
                    e.preventDefault()
                    askAssistant()
                  }
                }}
              >
                <div className='search-no-glow' style={{ flex: 1, minWidth: 0 }}>
                  <TextField
                    placeholder='Ask AI, e.g. "tenants on alpha with housing enabled" or "not up to date on identity"'
                    value={assistantPrompt}
                    onChange={(e) => setAssistantPrompt(e.target.value)}
                    onClear={() => setAssistantPrompt('')}
                    type='text'
                    variant='default'
                  />
                </div>
                <Button2
                  text={assistantBusy ? 'Thinking…' : 'Ask AI'}
                  variant='contained'
                  isDisabled={assistantBusy || !assistantPrompt.trim()}
                  onClick={askAssistant}
                  type='button'
                />
              </div>
              {assistantNote && (
                <div style={{ marginTop: '6px' }}>
                  <Text textStyle='description' textColor={assistantNote.isError ? 'error' : 'grey'} content={assistantNote.text} />
                </div>
              )}
            </div>
          )}
          <div className='search-no-glow' style={{ marginBottom: '10px' }}>
            <TextField
              isSearchMode
              placeholder='Search for a subscription or tenant'
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onClear={() => setQuery('')}
              type='text'
              variant='default'
            />
          </div>
          <div style={filterRowStyle}>
            <Text textStyle='description' textColor='grey' content='Stage' style={{ minWidth: '54px' }} />
            {environmentOptions.length > 0 ? renderChips(environmentOptions, selectedEnvironments, setSelectedEnvironments) : null}
          </div>
          <div style={filterRowStyle}>
            <Text textStyle='description' textColor='grey' content='Status' style={{ minWidth: '54px' }} />
            {deployStatusOptions.length > 0 ? renderChips(deployStatusOptions, selectedDeployStatuses, setSelectedDeployStatuses) : null}
          </div>
          <div style={filterRowStyle}>
            <Text textStyle='description' textColor='grey' content='Secret' style={{ minWidth: '54px' }} />
            <div style={chipsWrapStyle}>
              <Button2
                text={`Client secret expiring (≤ ${CLIENT_SECRET_WARNING_DAYS} days)`}
                variant='filter'
                isActive={secretExpiring}
                onClick={() => setSecretExpiring((v) => !v)}
                type='button'
              />
            </div>
          </div>
          {/* Destroyed tenants are hidden by default -- their pipeline already tore them
              down, so listing them reads as "this still exists". Opt in to confirm one is gone. */}
          <div style={{ ...filterRowStyle, marginBottom: 0 }}>
            <Text textStyle='description' textColor='grey' content='Deleted' style={{ minWidth: '54px' }} />
            <div style={chipsWrapStyle}>
              <Button2
                text='Include deleted'
                variant='filter'
                isActive={includeDeleted}
                onClick={() => setIncludeDeleted((v) => !v)}
                type='button'
              />
            </div>
          </div>
          {/* Tenant configuration (WI 159594): search the catalog for a key, then pick
              its values. The catalog runs to ~90 keys, hence a search rather than chips. */}
          {configKeys.length > 0 && (
            <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid var(--tma-list-card-border)' }}>
              <div style={filterRowStyle}>
                <Text textStyle='description' textColor='grey' content='Config' style={{ minWidth: '54px' }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  {/* key clears the picked option, so the search box is empty for the next key. */}
                  <Autocomplete
                    key={configFilters.length}
                    placeholder='Search a configuration key…'
                    options={configKeyOptions}
                    onChange={(e: { value: string }) => addConfigKey(e.value)}
                  />
                </div>
              </div>
              {configFilters.length > 0 && (
                <div style={{ maxHeight: '40vh', overflowY: 'auto' }} className='custom-scrollbar'>
                  {configFilters.map((filter) => (
                    <div key={filter.key} style={{ ...filterRowStyle, alignItems: 'flex-start' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '2px', minWidth: '54px', maxWidth: '40%' }}>
                        <Text textStyle='description' textColor='grey' content={configKeyLabel(filter.key)} />
                        <button
                          type='button'
                          aria-label={`Remove the ${configKeyLabel(filter.key)} filter`}
                          onClick={() => removeConfigKey(filter.key)}
                          style={removeKeyButtonStyle}
                        >
                          ×
                        </button>
                      </div>
                      {renderChips(configChipsFor(filter), filter.values, (values) => setConfigValues(filter.key, values), chipLabel)}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {/* Terraform module versions: pick a package, then the versions it should (or
              should not) be on. "is not" answers "which tenants still need updating" —
              note that it also matches tenants without the module, so select "Not set"
              alongside the version to keep those out. */}
          {modulePackages.length > 0 && (
            <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid var(--tma-list-card-border)' }}>
              <div style={filterRowStyle}>
                <Text textStyle='description' textColor='grey' content='Module' style={{ minWidth: '54px' }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  {/* key clears the picked option, so the search box is empty for the next package. */}
                  <Autocomplete
                    key={moduleFilters.length}
                    placeholder='Search a Terraform module…'
                    options={modulePackageOptions}
                    onChange={(e: { value: string }) => addModulePackage(e.value)}
                  />
                </div>
              </div>
              {moduleFilters.length > 0 && (
                <div style={{ maxHeight: '40vh', overflowY: 'auto' }} className='custom-scrollbar'>
                  {moduleFilters.map((filter) => (
                    <div key={filter.package} style={{ ...filterRowStyle, alignItems: 'flex-start' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '2px', minWidth: '54px', maxWidth: '40%' }}>
                        <Text textStyle='description' textColor='grey' content={filter.package} />
                        <button
                          type='button'
                          aria-label={`Remove the ${filter.package} filter`}
                          onClick={() => removeModulePackage(filter.package)}
                          style={removeKeyButtonStyle}
                        >
                          ×
                        </button>
                      </div>
                      <Button2
                        text={filter.negate ? 'is not' : 'is'}
                        variant='filter'
                        isActive={filter.negate}
                        onClick={() => toggleModuleNegate(filter.package)}
                        aria-label={`${filter.package}: match the selected versions, or everything but them`}
                        type='button'
                      />
                      {renderChips(
                        moduleChipsFor(filter),
                        filter.versions,
                        (versions) => setModuleVersions(filter.package, versions),
                        chipLabel
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        <div style={{ flex: 1, minHeight: 0, background: 'var(--tma-page-bg)' }} />
      </div>

      {/* Right column: results with a 52px title band aligning with the left band. */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          background: 'var(--tma-panel-bg)',
          padding: '0 20px',
          boxSizing: 'border-box'
        }}
      >
        <div style={{ flexShrink: 0, height: '52px', boxSizing: 'border-box', display: 'flex', flexDirection: 'column' }}>
          <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
            <Text textStyle='h6' style={{ fontSize: '16px' }}>
              {hasResults && !isLoading ? `Results (${subscriptions.length + tenants.length})` : 'Results'}
            </Text>
          </div>
          <div style={{ borderTop: '1px solid var(--tma-list-card-border)', margin: '0 -20px' }} />
        </div>

        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '12px 0' }} className='custom-scrollbar'>
          {isLoading && <LoadingState text='Searching…' />}

          {!isLoading && !hasCriteria && (
            <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <EmptyState
                imageName='search'
                size='medium'
                title='Start searching'
                text='Search by name, or filter by stage, status and configuration.'
              />
            </div>
          )}

          {!isLoading && searchError && (
            <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <EmptyState imageName='error504' size='medium' title='Something went wrong' text={searchError} />
            </div>
          )}

          {!isLoading && !searchError && hasCriteria && !hasResults && (
            <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <EmptyState imageName='searchempty' size='medium' title='No results' text='Nothing matches your search or filters.' />
            </div>
          )}

          {!isLoading && subscriptions.length > 0 && (
            <>
              <SectionHeader title='Subscriptions' />
              {subscriptions.map((sub: Subscription) => (
                <div
                  key={sub.id}
                  role='button'
                  tabIndex={0}
                  onClick={() => navigate(`/subscriptions/${sub.id}`)}
                  onKeyDown={(e) => e.key === 'Enter' && navigate(`/subscriptions/${sub.id}`)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '8px',
                    borderStyle: 'solid',
                    borderTopWidth: '1px',
                    borderRightWidth: '1px',
                    borderBottomWidth: '1px',
                    borderLeftWidth: '4px',
                    borderTopColor: 'var(--tma-list-card-border)',
                    borderRightColor: 'var(--tma-list-card-border)',
                    borderBottomColor: 'var(--tma-list-card-border)',
                    borderLeftColor: 'var(--tma-accent)',
                    borderRadius: '6px',
                    background: 'var(--tma-panel-bg)',
                    padding: '12px 14px',
                    marginBottom: '6px',
                    boxSizing: 'border-box',
                    cursor: 'pointer'
                  }}
                >
                  <Text textStyle='body' content={sub.customerName} singleLine hasTooltipEllipsis />
                  <Icon iconName='ArrowRight1' size={14} color='var(--tma-accent)' style={{ flexShrink: 0 }} />
                </div>
              ))}
            </>
          )}

          {!isLoading && tenants.length > 0 && (
            <>
              <div style={{ marginTop: subscriptions.length > 0 ? '16px' : 0 }}>
                <SectionHeader title='Tenants' />
              </div>
              {tenants.map((tenant: Tenant) => (
                <TenantListItem
                  key={tenant.id}
                  tenant={tenant}
                  configValues={[...configValuesFor(tenant.id), ...moduleValuesFor(tenant.id)]}
                />
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default AdvancedSearch
